import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pin, Pencil, Archive, Trash2 } from 'lucide-react';
import {
  useAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { PageTransition, FadeIn } from '@/components/ui/motion';
import { AnnouncementMedia } from '@/components/AnnouncementMedia';

const AUDIENCE_LABELS: Record<string, string> = {
  ALL: 'Todos',
  BY_COUNTRY: 'Por país',
  BY_DEPARTMENT: 'Por departamento',
  BY_COUNTRY_AND_DEPARTMENT: 'País y depto.',
};

export function AnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: a, isLoading } = useAnnouncement(id);
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'ADMIN' || role === 'HR';
  const del = useDeleteAnnouncement();
  const update = useUpdateAnnouncement();

  const handleDelete = async () => {
    if (!id || !confirm('¿Eliminar este anuncio permanentemente?')) return;
    try {
      await del.mutateAsync(id);
      navigate('/anuncios');
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo eliminar');
    }
  };

  const handleArchive = async () => {
    if (!id || !a) return;
    const newStatus = a.status === 'ARCHIVED' ? 'PUBLISHED' : 'ARCHIVED';
    try {
      await update.mutateAsync({ id, payload: { status: newStatus } });
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'Error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-4 w-40 rounded brand-shimmer" />
        <Card className="space-y-6 p-4 sm:p-6 lg:p-8">
          <div className="h-7 w-2/3 rounded brand-shimmer" />
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full brand-shimmer" />
            <div className="space-y-2">
              <div className="h-3 w-32 rounded brand-shimmer" />
              <div className="h-2.5 w-20 rounded brand-shimmer" />
            </div>
          </div>
          <div className="space-y-3 pt-4">
            <div className="h-3 w-full rounded brand-shimmer" />
            <div className="h-3 w-11/12 rounded brand-shimmer" />
            <div className="h-3 w-10/12 rounded brand-shimmer" />
          </div>
        </Card>
      </div>
    );
  }
  if (!a) {
    return (
      <p className="py-10 text-center text-font-secondary">
        Anuncio no encontrado.
      </p>
    );
  }

  const authorName = a.createdBy?.employee
    ? `${a.createdBy.employee.firstName} ${a.createdBy.employee.lastName}`
    : a.createdBy?.email ?? '–';
  const authorRole = a.createdBy?.employee?.position?.title ?? '';
  const authorPhoto = a.createdBy?.employee?.photoUrl;

  return (
    <PageTransition className="flex flex-col gap-6">
      <Link
        to="/anuncios"
        className="inline-flex w-fit items-center gap-2 text-sm font-medium text-font-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a anuncios
      </Link>

      <FadeIn delay={0.05}>
        <Card className="p-4 sm:p-6 lg:p-8">
          {/* Title */}
          <h1 className="text-2xl font-bold text-font-primary">{a.title}</h1>

          {/* Meta */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {authorPhoto ? (
              <img
                src={authorPhoto}
                alt={authorName}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-sm font-semibold text-white shadow-sm">
                {initials(authorName)}
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-font-primary">{authorName}</p>
              {authorRole && (
                <p className="text-xs text-font-secondary">{authorRole}</p>
              )}
            </div>
            <span className="text-xs text-font-tertiary">
              {a.publishedAt
                ? new Date(a.publishedAt).toLocaleDateString('es', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : new Date(a.createdAt).toLocaleDateString('es', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
            </span>
            <Badge variant="info">
              {AUDIENCE_LABELS[a.audienceType] ?? a.audienceType}
            </Badge>
            {a.pinned && (
              <Badge variant="warning">
                <Pin size={10} /> Fijado
              </Badge>
            )}
            {a.status === 'DRAFT' && (
              <Badge variant="warning" dot>
                Borrador
              </Badge>
            )}
            {a.status === 'ARCHIVED' && (
              <Badge variant="neutral" dot>
                Archivado
              </Badge>
            )}
          </div>

          {/* Divider */}
          <hr className="my-6 border-border" />

          {/* Body */}
          <div className="prose prose-sm max-w-none text-font-primary">
            {a.body.split('\n').map((line: string, i: number) => {
              if (!line.trim()) return <br key={i} />;
              if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
                return (
                  <p key={i} className="ml-4 flex items-start gap-2 text-sm text-font-primary">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                    {line.trim().slice(2)}
                  </p>
                );
              }
              return (
                <p key={i} className="text-sm leading-relaxed text-font-primary">
                  {line}
                </p>
              );
            })}
          </div>

          {/* Galería de adjuntos: imágenes en collage + documentos descargables */}
          <AnnouncementMedia attachments={a.attachments} />

          {/* Footer */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
            {isAdmin && (
              <p className="text-xs text-font-tertiary">
                Leído por {a.readCount ?? 0} de {a.totalAudience ?? '?'} colaboradores
              </p>
            )}
            {!isAdmin && <span />}

            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to={`/anuncios/${a.id}/editar`}>
                    <Pencil size={12} /> Editar
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleArchive}
                  disabled={update.isPending}
                >
                  <Archive size={12} />{' '}
                  {a.status === 'ARCHIVED' ? 'Republicar' : 'Archivar'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={del.isPending}
                >
                  <Trash2 size={12} /> Eliminar
                </Button>
              </div>
            )}
          </div>
        </Card>
      </FadeIn>
    </PageTransition>
  );
}
